/**
 * @fileoverview Lookup service for the ui-admin shared package.
 * @module ui-admin/services/lookupService
 *
 * Responsibilities:
 * - Provide lookup value CRUD for the system-web LookupsPage canvas.
 * - Define LookupValue, LookupGroup, and LookupScope types.
 *
 * Dependencies: localStorage (transitional).
 * Architecture: Transitional localStorage-backed stub.
 * Notes: TODO — migrate to Dexie reads via systemLookupService (system_lookups,
 *        system_lookup_categories, system_lookup_groups in SYSTEM_COLLECTION_MAP).
 */
// ============================================================

import type { ListParams, PaginatedResponse } from '../types/shared';
import {
  filterLookupValues,
  type LookupFilterContext,
} from '../../../core-domain/src/forms/index';

export type LookupScope = 'system' | 'organization' | 'tenant';
export type InheritancePolicy = 'seed_hideable' | 'locked_mandatory' | 'custom_owned';

export interface LookupCategory {
  lookupType: string;
  displayName: string;
  description: string;
  module?: string;
  availableScopes: LookupScope[];
  manageableScopes: LookupScope[];
  isSystemManaged: boolean;
  _deleted?: boolean;
}

export interface LookupDisplay {
  textColor?: string;
  backgroundColor?: string;
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  fontStyle?: 'normal' | 'italic';
  badgeStyle?: 'solid' | 'outline' | 'subtle';
  icon?: string;
  iconColor?: string;
  strikethrough?: boolean;
  uppercase?: boolean;
  cssClass?: string;
}

export interface LookupEditPolicy {
  orgCanEdit?: boolean;
  orgCanHide?: boolean;
  tenantCanEdit?: boolean;
  tenantCanHide?: boolean;
  lockedFields?: string[];
}

export interface LookupGroupInfo {
  groupId: string;
  groupCode: string;
  groupName: string;
  groupIcon?: string;
  groupColor?: string;
  groupSortOrder: number;
}

export interface LookupGroup {
  id: string;
  lookupType: string;
  code: string;
  name: string;
  description?: string;
  scope: LookupScope;
  parentGroupId?: string;
  icon?: string;
  color?: string;
  sortOrder: number;
  isActive: boolean;
  isSystemDefault: boolean;
  editPolicy?: LookupEditPolicy;
  createdAt: string;
  updatedAt: string;
}

export interface LookupValue {
  id: string;
  lookupType: string;
  code: string;
  name: string;
  description?: string;
  scope: LookupScope;
  originScope: LookupScope;
  inheritancePolicy: InheritancePolicy;
  tenantCanHideInherited?: boolean;
  orgCanHideInherited?: boolean;
  sortOrder: number;
  isActive: boolean;
  _deleted?: boolean;
  isSystemDefault?: boolean;
  isHiddenAtLowerLevels?: boolean;
  appliesTo?: string[];
  category?: string | string[];
  inspectionModules?: string[];
  group?: LookupGroupInfo;
  editPolicy?: LookupEditPolicy;
  display?: LookupDisplay;
  config?: Record<string, unknown>;
  requiredModules?: string[];
  aiMetadata?: {
    aiDescription?: string;
    aiTags?: string[];
    aiSynonyms?: string[];
  };
}

export interface ListLookupValuesOptions extends LookupFilterContext {
  activeModules?: string[];
}

const delay = (ms = 30) => new Promise((r) => setTimeout(r, ms));

const LOOKUP_VALUES_STORAGE_KEY = 'firesquirrel_lookup_values';
const LOOKUP_CATEGORIES_STORAGE_KEY = 'firesquirrel_lookup_categories';
const LOOKUP_GROUPS_STORAGE_KEY = 'firesquirrel_lookup_groups';

function loadPersistedData<T>(key: string): T[] | null {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed as T[];
      localStorage.removeItem(key);
    }
  } catch {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
  return null;
}

function persistData<T>(key: string, data: T[]): void {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch { /* ignore */ }
}

// Seed Data
// ─────────────────────────────────────────────────────────────────
// Module philosophy:
//   Modules gate functionality. If a department doesn't have a module,
//   they don't see the lookup categories specific to that module.
//   Categories common to ALL inspection types (case type, event type,
//   event status, violation severity, etc.) are Core — no module.
//   Only categories truly unique to a module get assigned to one.
//
//   Current active module: wildland (parcels + sites).
//   Future modules: building, hydrant, permits, billing, public-ed, etc.
// ─────────────────────────────────────────────────────────────────
const SEED_CATEGORIES: LookupCategory[] = [
  // ── Core (no module) — always available ──────────────────────
  {
    lookupType: 'organizationType',
    displayName: 'Organization Type',
    description: 'Top-level organization classification (county, state, district, authority).',
    availableScopes: ['system'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'contactType',
    displayName: 'Contact Type',
    description: 'Contact role classification (primary, billing, fire chief, etc.).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'contactStatus',
    displayName: 'Contact Status',
    description: 'Active/inactive status for contact records.',
    availableScopes: ['system'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'entityContactRole',
    displayName: 'Entity Contact Role',
    description: 'Role a contact plays on an entity (owner, manager, tenant, key-holder, etc.).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'phoneType',
    displayName: 'Phone Type',
    description: 'Phone number classification (office, mobile, emergency, fax, home).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'emailType',
    displayName: 'Email Type',
    description: 'Email address classification (business, personal, emergency).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'entityStatus',
    displayName: 'Entity Status',
    description: 'Status for inspectable entity records (active, pending, archived).',
    availableScopes: ['system'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'propertyType',
    displayName: 'Property Type',
    description: 'Property classification (residential, commercial, industrial, wildland).',
    availableScopes: ['system'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'siteType',
    displayName: 'Site Type',
    description: 'General-purpose site categories (occupancy, event venue, government, retail, etc.).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'zoningCode',
    displayName: 'Zoning Code',
    description: 'Jurisdiction-specific zoning codes (R-1, C-2, M-1, PUD, etc.). Configured by department.',
    availableScopes: ['tenant'],
    manageableScopes: ['tenant'],
    isSystemManaged: false,
  },
  // ── Geography / Jurisdiction (Core) ──────────────────────────
  {
    lookupType: 'state',
    displayName: 'State',
    description: 'US states and territories.',
    availableScopes: ['system'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'county',
    displayName: 'County',
    description: 'Counties within states.',
    availableScopes: ['system'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'city',
    displayName: 'City',
    description: 'Cities and municipalities.',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'country',
    displayName: 'Country',
    description: 'Countries (US, Canada, etc.).',
    availableScopes: ['system'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  // ── Shared inspection categories (Core — common to ALL modules) ──
  {
    lookupType: 'caseType',
    displayName: 'Case Type',
    description: 'Inspection case classification (annual, wildland, hood & duct, construction, etc.).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'eventType',
    displayName: 'Event Type',
    description: 'Event types used across all inspection modules (initial inspection, reinspection, phone call, etc.).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'eventStatus',
    displayName: 'Event Status',
    description: 'Lifecycle status for events (scheduled, in progress, completed, cancelled, overdue).',
    availableScopes: ['system'],
    manageableScopes: ['system'],
    isSystemManaged: true,
  },
  {
    lookupType: 'inspectionFrequency',
    displayName: 'Inspection Frequency',
    description: 'How often an entity is inspected (weekly, monthly, quarterly, annual, biennial).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'closeoutReason',
    displayName: 'Close-Out Reason',
    description: 'Reason for closing out an inspection series (compliance achieved, property demolished, transferred, etc.).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'violationSeverity',
    displayName: 'Violation Severity',
    description: 'Severity levels for violations (critical, high, medium, low).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization'],
    isSystemManaged: false,
  },
  {
    lookupType: 'violationCategory',
    displayName: 'Violation Category',
    description: 'Hierarchical violation categories (fire safety, electrical, structural, egress, hazmat, etc.).',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  // ── Wildland module (active) ─────────────────────────────────
  // (No wildland-specific lookup categories yet — parcels/sites
  //  use Core categories. Wildland-specific categories can be
  //  added here as the module matures.)

  // ── Building module (future) ─────────────────────────────────
  {
    lookupType: 'buildingType',
    displayName: 'Building Type',
    description: 'Building classification (single-family, multi-family, commercial, industrial, mixed-use).',
    module: 'building',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'occupancyClassification',
    displayName: 'Occupancy Classification',
    description: 'IBC/NFPA occupancy groups (Assembly A, Business B, Educational E, etc.).',
    module: 'building',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'constructionType',
    displayName: 'Construction Type',
    description: 'IBC/NFPA construction types (Type I-A Fire Resistive through Type V-B Wood Frame).',
    module: 'building',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  {
    lookupType: 'roofCoveringType',
    displayName: 'Roof Covering Type',
    description: 'Roof material categories (asphalt shingle, metal, tile, slate, etc.).',
    module: 'building',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  // ── Hydrant module (future) ──────────────────────────────────
  {
    lookupType: 'hydrantType',
    displayName: 'Hydrant Type',
    description: 'Fire hydrant classification (dry-barrel, wet-barrel).',
    module: 'hydrant',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
  // ── Complex module (future) ──────────────────────────────────
  {
    lookupType: 'complexType',
    displayName: 'Complex Type',
    description: 'Multi-structure complex classification (apartment complex, shopping center, campus, etc.).',
    module: 'complex',
    availableScopes: ['system', 'organization', 'tenant'],
    manageableScopes: ['system', 'organization', 'tenant'],
    isSystemManaged: false,
  },
];

// Initialize State — always read from localStorage first; fall back to seeds.
// Helper ensures we never serve stale module-level data after HMR or page reload.

// ── Migration helpers ──────────────────────────────────────────
// Rename old lookupType keys to current names
const LOOKUP_TYPE_RENAMES: Record<string, string> = {
  'inspectionStatus': 'eventStatus',
  'inspectionCloseOutReason': 'closeoutReason',
  'seriesType': 'caseType',
};

// Map old module display labels → new module keys
const MODULE_MIGRATION: Record<string, string> = {
  'Inspections': '',     // shared categories are now Core
  'fireCode': '',        // shared categories moved out of fireCode
  'System': '',
  'General': '',
};

function migrateData<T extends { lookupType: string; module?: string }>(items: T[]): { data: T[]; changed: boolean } {
  let changed = false;
  const migrated = items.map((item) => {
    let current = item;
    // Rename lookupType if needed
    const newType = LOOKUP_TYPE_RENAMES[current.lookupType];
    if (newType) {
      changed = true;
      current = { ...current, lookupType: newType };
    }
    // Migrate module if needed (only for items with a module property)
    if ('module' in current && current.module && MODULE_MIGRATION[current.module] !== undefined) {
      changed = true;
      current = { ...current, module: MODULE_MIGRATION[current.module] || undefined };
    }
    return current;
  });
  return { data: migrated, changed };
}

function getCategories(): LookupCategory[] {
  const persisted = loadPersistedData<LookupCategory>(LOOKUP_CATEGORIES_STORAGE_KEY);
  if (persisted) {
    // Step 1: Migrate renames and module changes
    const { data: migrated } = migrateData(persisted);

    // Step 2: Build seed lookup map and sync with latest seed definitions
    const seedMap = new Map(SEED_CATEGORIES.map((s) => [s.lookupType, s]));
    const persistedTypes = new Set(migrated.map((c) => c.lookupType));

    // Update existing persisted categories with latest seed definitions
    const updated = migrated.map((c) => {
      const seed = seedMap.get(c.lookupType);
      if (seed) return { ...seed, _deleted: c._deleted };
      return c; // user-created category — leave as-is
    });

    // Append any seed categories not yet in persisted data
    const missing = SEED_CATEGORIES.filter((s) => !persistedTypes.has(s.lookupType));

    // Deduplicate by lookupType (renames can create collisions)
    const deduped = new Map<string, LookupCategory>();
    for (const c of [...updated, ...missing]) deduped.set(c.lookupType, c);
    const merged = Array.from(deduped.values());

    persistData(LOOKUP_CATEGORIES_STORAGE_KEY, merged);
    return merged;
  }
  persistData(LOOKUP_CATEGORIES_STORAGE_KEY, SEED_CATEGORIES);
  return [...SEED_CATEGORIES];
}

function setCategories(next: LookupCategory[]): void {
  persistData(LOOKUP_CATEGORIES_STORAGE_KEY, next);
}

// ─────────────────────────────────────────────────────────────────
// Seed values — provides a starter set so dropdowns are not empty
// in dev / stub mode. In production, values are propagated from
// the system database via replaySystemLookupsToTenants.
// ─────────────────────────────────────────────────────────────────
const SEED_VALUES: LookupValue[] = [
  // propertyType — system-managed
  { id: 'pt-residential', lookupType: 'propertyType', code: 'residential', name: 'Residential', scope: 'system', originScope: 'system', inheritancePolicy: 'locked_mandatory', sortOrder: 1, isActive: true, isSystemDefault: true },
  { id: 'pt-commercial', lookupType: 'propertyType', code: 'commercial', name: 'Commercial', scope: 'system', originScope: 'system', inheritancePolicy: 'locked_mandatory', sortOrder: 2, isActive: true, isSystemDefault: true },
  { id: 'pt-industrial', lookupType: 'propertyType', code: 'industrial', name: 'Industrial', scope: 'system', originScope: 'system', inheritancePolicy: 'locked_mandatory', sortOrder: 3, isActive: true, isSystemDefault: true },
  { id: 'pt-wildland', lookupType: 'propertyType', code: 'wildland', name: 'Wildland', scope: 'system', originScope: 'system', inheritancePolicy: 'locked_mandatory', sortOrder: 4, isActive: true, isSystemDefault: true },
  { id: 'pt-mixed-use', lookupType: 'propertyType', code: 'mixed-use', name: 'Mixed Use', scope: 'system', originScope: 'system', inheritancePolicy: 'locked_mandatory', sortOrder: 5, isActive: true, isSystemDefault: true },
  { id: 'pt-agricultural', lookupType: 'propertyType', code: 'agricultural', name: 'Agricultural', scope: 'system', originScope: 'system', inheritancePolicy: 'locked_mandatory', sortOrder: 6, isActive: true, isSystemDefault: true },
  { id: 'pt-vacant', lookupType: 'propertyType', code: 'vacant', name: 'Vacant Land', scope: 'system', originScope: 'system', inheritancePolicy: 'locked_mandatory', sortOrder: 7, isActive: true, isSystemDefault: true },
  { id: 'pt-government', lookupType: 'propertyType', code: 'government', name: 'Government / Public', scope: 'system', originScope: 'system', inheritancePolicy: 'locked_mandatory', sortOrder: 8, isActive: true, isSystemDefault: true },
  // zoningCode — tenant-managed (sample codes; tenants add their own)
  { id: 'zn-r1', lookupType: 'zoningCode', code: 'R-1', name: 'R-1 (Single Family Residential)', scope: 'tenant', originScope: 'tenant', inheritancePolicy: 'custom_owned', sortOrder: 1, isActive: true },
  { id: 'zn-r2', lookupType: 'zoningCode', code: 'R-2', name: 'R-2 (Multi-Family Residential)', scope: 'tenant', originScope: 'tenant', inheritancePolicy: 'custom_owned', sortOrder: 2, isActive: true },
  { id: 'zn-c1', lookupType: 'zoningCode', code: 'C-1', name: 'C-1 (Neighborhood Commercial)', scope: 'tenant', originScope: 'tenant', inheritancePolicy: 'custom_owned', sortOrder: 3, isActive: true },
  { id: 'zn-c2', lookupType: 'zoningCode', code: 'C-2', name: 'C-2 (General Commercial)', scope: 'tenant', originScope: 'tenant', inheritancePolicy: 'custom_owned', sortOrder: 4, isActive: true },
  { id: 'zn-m1', lookupType: 'zoningCode', code: 'M-1', name: 'M-1 (Light Industrial)', scope: 'tenant', originScope: 'tenant', inheritancePolicy: 'custom_owned', sortOrder: 5, isActive: true },
  { id: 'zn-pud', lookupType: 'zoningCode', code: 'PUD', name: 'PUD (Planned Unit Development)', scope: 'tenant', originScope: 'tenant', inheritancePolicy: 'custom_owned', sortOrder: 6, isActive: true },
  { id: 'zn-ag', lookupType: 'zoningCode', code: 'AG', name: 'AG (Agricultural)', scope: 'tenant', originScope: 'tenant', inheritancePolicy: 'custom_owned', sortOrder: 7, isActive: true },
];

function getValues(): LookupValue[] {
  const persisted = loadPersistedData<LookupValue>(LOOKUP_VALUES_STORAGE_KEY);
  if (!persisted) {
    persistData(LOOKUP_VALUES_STORAGE_KEY, SEED_VALUES);
    return [...SEED_VALUES];
  }

  // Preserve the existing rename migration path for any locally cached data.
  const { data: migrated, changed: migrationChanged } = migrateData(persisted);

  // Merge any seed values that are missing from persisted data
  const existingIds = new Set(migrated.map((v) => v.id));
  const missing = SEED_VALUES.filter((s) => !existingIds.has(s.id));
  if (missing.length > 0 || migrationChanged) {
    const merged = [...migrated, ...missing];
    persistData(LOOKUP_VALUES_STORAGE_KEY, merged);
    return merged;
  }
  return migrated;
}

function setValues(next: LookupValue[]): void {
  persistData(LOOKUP_VALUES_STORAGE_KEY, next);
}

// @deprecated Use listLookupCategories instead
export const LOOKUP_CATEGORIES = SEED_CATEGORIES;

export async function listLookupCategories(params?: ListParams): Promise<PaginatedResponse<LookupCategory>> {
  await delay();
  const cats = getCategories();
  let filtered = cats.filter((c) => !c._deleted);

  if (params?.search) {
    const term = params.search.toLowerCase();
    filtered = filtered.filter(
      (c) =>
        c.displayName.toLowerCase().includes(term)
        || c.lookupType.toLowerCase().includes(term)
        || c.description.toLowerCase().includes(term),
    );
  }

  const page = params?.page || 1;
  const limit = params?.limit || 50;
  const start = (page - 1) * limit;

  return {
    data: filtered.slice(start, start + limit),
    total: filtered.length,
    page,
    limit,
    totalPages: Math.ceil(filtered.length / limit),
  };
}

export async function saveLookupCategory(category: LookupCategory): Promise<LookupCategory> {
  await delay();
  const cats = getCategories();
  const idx = cats.findIndex((c) => c.lookupType === category.lookupType);
  if (idx >= 0) {
    cats[idx] = { ...cats[idx], ...category };
  } else {
    cats.push({ ...category });
  }
  setCategories(cats);
  return cats[idx >= 0 ? idx : cats.length - 1];
}

export async function softDeleteLookupCategory(lookupType: string): Promise<void> {
  await delay();
  const cats = getCategories();
  const idx = cats.findIndex((c) => c.lookupType === lookupType);
  if (idx >= 0) {
    cats[idx] = { ...cats[idx], _deleted: true };
    setCategories(cats);
  }
}

export async function listLookupValues(
  lookupType: string,
  options?: ListLookupValuesOptions,
): Promise<LookupValue[]> {
  await delay();
  return filterLookupValues(
    getValues().filter((v) => v.lookupType === lookupType),
    {
      ...options,
      inspectionModules: options?.inspectionModules ?? options?.activeModules,
    },
  );
}

export async function saveLookupValue(nextValue: LookupValue): Promise<LookupValue> {
  await delay();
  const vals = getValues();
  const idx = vals.findIndex((v) => v.id === nextValue.id);
  if (idx >= 0) {
    vals[idx] = { ...vals[idx], ...nextValue };
    setValues(vals);
    return vals[idx];
  }
  vals.push(nextValue);
  setValues(vals);
  return nextValue;
}

export async function setLookupHidden(id: string, hidden: boolean): Promise<void> {
  await delay();
  const vals = getValues().map((v) => (v.id === id ? { ...v, isHiddenAtLowerLevels: hidden } : v));
  setValues(vals);
}

export async function softDeleteLookupValue(id: string): Promise<void> {
  await delay();
  const vals = getValues().map((v) => (v.id === id ? { ...v, _deleted: true, isActive: false } : v));
  setValues(vals);
}

// ============================================================
// Lookup Groups — CRUD
// ============================================================

function getGroups(): LookupGroup[] {
  return loadPersistedData<LookupGroup>(LOOKUP_GROUPS_STORAGE_KEY) || [];
}

function setGroups(next: LookupGroup[]): void {
  persistData(LOOKUP_GROUPS_STORAGE_KEY, next);
}

export async function listLookupGroups(lookupType: string): Promise<LookupGroup[]> {
  await delay();
  return getGroups()
    .filter((g) => g.lookupType === lookupType && g.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export async function saveLookupGroup(group: LookupGroup): Promise<LookupGroup> {
  await delay();
  const now = new Date().toISOString();
  const groups = getGroups();
  const idx = groups.findIndex((g) => g.id === group.id);

  if (idx >= 0) {
    const prev = groups[idx];
    const updated = { ...prev, ...group, updatedAt: now };
    groups[idx] = updated;
    setGroups(groups);

    const nameChanged = prev.name !== updated.name;
    const iconChanged = prev.icon !== updated.icon;
    const colorChanged = prev.color !== updated.color;
    const sortChanged = prev.sortOrder !== updated.sortOrder;

    if (nameChanged || iconChanged || colorChanged || sortChanged) {
      const vals = getValues().map((v) => {
        if (v.group?.groupId !== updated.id) return v;
        return {
          ...v,
          group: {
            ...v.group,
            groupName: updated.name,
            groupIcon: updated.icon,
            groupColor: updated.color,
            groupSortOrder: updated.sortOrder,
          },
        };
      });
      setValues(vals);
    }

    return updated;
  }

  const newGroup: LookupGroup = { ...group, createdAt: now, updatedAt: now };
  groups.push(newGroup);
  setGroups(groups);
  return newGroup;
}

export async function deleteLookupGroup(groupId: string, handleValues: 'ungroup' | 'soft-delete'): Promise<void> {
  await delay();
  const groups = getGroups();
  const vals = getValues();

  if (handleValues === 'ungroup') {
    const updated = vals.map((v) =>
      v.group?.groupId === groupId ? { ...v, group: undefined } : v,
    );
    setValues(updated);
  } else {
    const updated = vals.map((v) =>
      v.group?.groupId === groupId
        ? { ...v, group: undefined, _deleted: true, isActive: false }
        : v,
    );
    setValues(updated);
  }

  setGroups(groups.filter((g) => g.id !== groupId));
}

export async function assignValueToGroup(valueId: string, groupId: string | null): Promise<void> {
  await delay();
  const vals = getValues();
  const groups = getGroups();
  const group = groupId ? groups.find((g) => g.id === groupId) : null;

  const updated = vals.map((v) => {
    if (v.id !== valueId) return v;
    if (!group) return { ...v, group: undefined };
    return {
      ...v,
      group: {
        groupId: group.id,
        groupCode: group.code,
        groupName: group.name,
        groupIcon: group.icon,
        groupColor: group.color,
        groupSortOrder: group.sortOrder,
      },
    };
  });
  setValues(updated);
}

export interface SyncToTenantsResult {
  message: string;
  tenantCount: number;
  systemLookups: number;
  systemCategories: number;
  systemGroups: number;
}

export async function syncLookupsToAllTenants(): Promise<SyncToTenantsResult> {
  const res = await fetch('/api/lookups/sync-to-tenants', { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Sync failed (${res.status})`);
  }
  return res.json();
}

export interface SyncToTenantResult {
  message: string;
  tenantId: string;
  systemLookups: number;
  systemCategories: number;
  systemGroups: number;
}

export async function syncLookupsToTenant(tenantId: string): Promise<SyncToTenantResult> {
  const res = await fetch(`/api/lookups/sync-to-tenant/${tenantId}`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Sync failed (${res.status})`);
  }
  return res.json();
}

export async function bulkAssignValuesToGroup(valueIds: string[], groupId: string | null): Promise<void> {
  await delay();
  const idSet = new Set(valueIds);
  const vals = getValues();
  const groups = getGroups();
  const group = groupId ? groups.find((g) => g.id === groupId) : null;

  const updated = vals.map((v) => {
    if (!idSet.has(v.id)) return v;
    if (!group) return { ...v, group: undefined };
    return {
      ...v,
      group: {
        groupId: group.id,
        groupCode: group.code,
        groupName: group.name,
        groupIcon: group.icon,
        groupColor: group.color,
        groupSortOrder: group.sortOrder,
      },
    };
  });
  setValues(updated);
}
